<script setup>
import {useToast} from "vue-toastification";
import {onMounted, ref} from "vue";
import Button from "../../components/forms/Button.vue";
import {useDataTable} from "../../utils/datatableHelper.js";
import DocumentTemplateService from "../../services/DocumentTemplateService.js";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import GenerateDocumentTemplateModal from "../../components/modals/GenerateDocumentTemplateModal.vue";
import ModalDialog from "../../components/modals/ModalDialog.vue";
import {formatDate} from "../../helper.js";

import {useI18n} from 'vue-i18n'

const {t} = useI18n();

const props = defineProps({
  documentTemplate: {
    type: Object,
    required: true
  },
});

const signatureStateMap = [
  {label: t('generatedDocuments.waiting'), value: 'PENDING'},
  {label: t('generatedDocuments.signed'), value: 'SIGNED'},
  {label: t('generatedDocuments.state_rejected'), value: 'REJECTED'},
  {label: t('generatedDocuments.not_required'), value: 'NOT_REQUIRED'},
];

const toast = useToast();

const isLoading = ref(false);
const isGenerating = ref(false);
const datatableRef = ref(null);
const datatable = useDataTable(fetchListData, 'created_at', 10, false, {}, 'GENERATED-DOCUMENTS-' + props.documentTemplate?.id, datatableRef);

const rows = ref([]);
const columns = ref([
  {label: t('generatedDocuments.client'), field: 'documentable.label', filterOptions: {enabled: true, skipDiacritics: true, placeholder: t('generatedDocuments.filter_by_client')}},
  {label: t('generatedDocuments.document'), field: 'file.filename', filterOptions: {enabled: true, skipDiacritics: true, placeholder: t('generatedDocuments.filter_by_responsible_person')}},
  {label: t('generatedDocuments.generated_day'), field: 'created_at', formatFn: (value) => formatDate(value, 'j.n.Y H:i:s'), filterOptions: {enabled: true, skipDiacritics: true, placeholder: t('generatedDocuments.filter_by_generate_date')}},
  {
    label: t('generatedDocuments.sign_by_client'), field: 'signatures', formatFn: (value) => {
      return signatureStateMap.filter((state) => state.value === (value.find((signature) => signature.role === 'OWNER')?.state ?? 'NOT_REQUIRED'))[0].label
    }, filterOptions: {enabled: true, skipDiacritics: true, placeholder: t('generatedDocuments.filter_by_client_signature')}
  },
  {
    label: t('generatedDocuments.sign_by_responsible_person'), field: 'signatures', formatFn: (value) => {
      return signatureStateMap.filter((state) => state.value === (value.find((signature) => signature.role === 'RESPONSIBLE')?.state ?? 'NOT_REQUIRED'))[0].label
    }, filterOptions: {enabled: true, skipDiacritics: true, placeholder: t('generatedDocuments.filter_by_responsible_person')}
  },
  {label: '', field: 'action', sortable: false, html: true},
]);

function fetchListData(body) {
  isLoading.value = true;
  DocumentTemplateService.fetchGeneratedDocuments(props.documentTemplate?.id, body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    rows.value = response.data;
    isLoading.value = false;
  });
}

function generate() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(GenerateDocumentTemplateModal, {
    documentTemplate: props.documentTemplate
  });

  reveal();
  onConfirm((data) => {
    isGenerating.value = true;
    DocumentTemplateService.generate(props.documentTemplate.id, data).then((response) => {
      toast.success(response.message);
      fetchListData(datatable.body);
      emitter.emit('document-template:refetch');
      isGenerating.value = false;
    });
  });
  onCancel(() => {
  });
}

function deleteDocument(id) {
  const selectedDocument = rows.value.filter(document => document.id === id)[0];
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('generatedDocuments.document_delete'),
    message: t('generatedDocuments.document_delete_really2') + ' ' + selectedDocument.name +
        ' ' + t('generatedDocuments.employee') + ' ' + selectedDocument.documentable.fullname + '?' +
        ' ' + t('generatedDocuments.this_action_is_irreversible'),
  });
  reveal();
  onConfirm(() => {
    DocumentTemplateService.deleteGeneratedDocument(selectedDocument.id).then((response) => {
      toast.success(response.message);
      fetchListData(datatable.body);
      emitter.emit('document-template:refetch');
    });
  });
  onCancel(() => {
  });
}

onMounted(() => {
});
</script>

<template>
  <div class="page-section__heading d-flex justify-content-between align-items-center">
    <div class="page-section__title">
      {{ t('generatedDocuments.generated_documents') }}
    </div>
    <div class="">
      <Button v-if="!isGenerating" label="Vygenerovat" icon="mdi-plus" @click="generate()" :disabled="!documentTemplate.has_template"/>
      <button v-else class="btn btn-primary" disabled="disabled">
        <span class="mdi mdi-loading icon-spinner text-2xl"></span> {{ t('generatedDocuments.generating') }}
      </button>
    </div>
  </div>
  <div class="page-section__content">
    <div class="mt-3">
      <div class="">
        <vue-good-table
            ref="datatableRef"
            :sort-options="{enabled: true, initialSortBy: datatable.initialSortBy.value}"
            :columns="columns"
            :rows="rows"
            mode="remote"
            @page-change="datatable.onPageChange"
            @sort-change="datatable.onSortChange"
            @column-filter="datatable.onColumnFilter"
            @per-page-change="datatable.onPerPageChange"
            :totalRows="datatable.totalRecords.value"
            :pagination-options="{
                    enabled: true,
                    position: 'bottom',
                    nextLabel: t('generatedDocuments.next'),
                    prevLabel: t('generatedDocuments.previous'),
                    ofLabel: 'z',
                    perPageDropdownEnabled: true,
                    rowsPerPageLabel: t('generatedDocuments.entriesPerPage'),
                  }">
          <template #table-row="props">
            <span v-if="props.column.field === 'action'">
              <span style="font-weight: bold; float: right">
                <router-link :to="`/documents/${props.row.id}/detail`" :title="t('generatedDocuments.show_documents')">
                  <div type="button" class="btn btn-primary ms-2"><i class="mdi mdi-eye"/></div>
                </router-link>
                <button type="button" class="btn btn-error ms-2" @click="deleteDocument(props.row.id)"><i class="mdi mdi-trash-can-outline"/></button>
              </span>
            </span>
          </template>
          <template v-slot:emptystate v-if="!isLoading">
            <div class="text-center">
              {{ t('generatedDocuments.no_records_found') }}
            </div>
          </template>
          <template v-slot:emptystate v-if="isLoading">
            <div class="text-center">
              {{ t('generatedDocuments.data_loading') }}
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
